import { Box, Flex, IconButton, Input, Td, Text, Tr } from "@chakra-ui/react";
import { CaretDown, Check, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { familyFor } from "./unitFamilies";
import { ACTIONS_SLOT_WIDTH, splitPerUnit, UNIT_SEGMENT_WIDTH, VALUE_FIELD_WIDTH } from "./fieldStyles";

/**
 * A datapoint the organisation asked for and nobody has filled in yet.
 *
 * The row that makes the table answer "what is still missing?" — before this, an unfilled datapoint simply
 * was not there, and an empty year rendered nothing at all.
 *
 * Dimmed rather than styled as a warning: a gap early in a reporting cycle is the normal state, not a
 * problem. It is a placeholder waiting to be filled, and colouring every one of sixty-six datapoints red on
 * day one would make the table unreadable and the warning meaningless.
 *
 * The "Not reported" field itself is the way in — clicking it turns this row directly into an inline
 * editor, the same Value+Unit field `EditableMetricRow`'s compact edit mode uses, rather than opening a
 * separate `AddMetricRow` row below it. The datapoint already has a name and expected unit (it's the
 * target structure's own entry), so there is nothing a form needs to ask for beyond the value itself —
 * clicking straight into the field it will fill is more direct than clicking an "Add" button to summon a
 * form that then asks for the same two things again. Hover state (lime border, darker text) is what
 * signals it's clickable, since nothing about a plain bordered box says so on its own.
 *
 * Only ever rendered in compact (structure) mode — a gap is a datapoint the structure asked for, and the
 * no-structure fallback has no structure to ask. Three cells, matching `FoundationDataTable`'s compact
 * header (Datapoint/Value/Actions) and `EditableMetricRow`'s compact read AND edit rows exactly —
 * including the same bordered field styling for "Not reported", so a gap reads as the same kind of field,
 * just empty, rather than a different shape (metrics-table-2a). A colSpan mismatch here fails neither
 * lint nor build — it silently shears the column alignment for the rows below it. See the "two column
 * modes" note in frontend/CLAUDE.md.
 */
export default function TargetGapRow({ target, category, canEdit, onAdd, isAdding, onEditingChange, reviewMode = false }) {
  const [isEditing, setIsEditing] = useState(false);
  const editorId = `gap-${target.id ?? target.dataKey}`;

  // Tells the page an editor is open, so Submit/Approve stay disabled until it is saved or cancelled.
  useEffect(() => {
    if (!onEditingChange) return undefined;
    onEditingChange(editorId, isEditing);
    return () => onEditingChange(editorId, false);
  }, [onEditingChange, editorId, isEditing]);
  const [draft, setDraft] = useState({ dataValue: "", unit: target.unit || "" });

  const startEdit = () => {
    setDraft({ dataValue: "", unit: target.unit || "" });
    setIsEditing(true);
  };
  const cancel = () => setIsEditing(false);
  const canSubmit = draft.dataValue.trim().length > 0;
  const save = () => {
    if (!canSubmit) return;
    onAdd(
      { category, dataKey: target.dataKey, dataValue: draft.dataValue, unit: draft.unit, scope: "" },
      () => setIsEditing(false)
    );
  };

  // "tCO2e per currency unit": base stays in the unit segment, the "per …" tail sits beside the name.
  const unitParts = splitPerUnit(target.unit);
  const labelCell = (
    <Text fontSize="14px" color="gray.500">
      {target.dataKey}
      {unitParts.per ? (
        <Text as="span" ml={2} fontSize="12.5px" color="gray.600">
          {unitParts.per}
        </Text>
      ) : null}
    </Text>
  );

  /* Review overlay: this document simply does not report the datapoint, so just its name — the empty value
     cell says the rest. Two cells, matching the overlay's two-column layout. */
  if (reviewMode) {
    return (
      <Tr>
        <Td>{labelCell}</Td>
        <Td />
      </Tr>
    );
  }

  if (isEditing) {
    // Same shape as EditableMetricRow's compact edit mode (green active border + focus ring, relabel-only
    // unit Select for known families) — a gap being filled in should look like the same kind of editing
    // the table already does everywhere else, not a one-off form.
    const unitOptions = familyFor(target.unit);
    return (
      <Tr bg="limeShade.50">
        <Td>{labelCell}</Td>
        <Td>
          <Flex justify="flex-end">
            <Flex
              border="1.5px solid"
              borderColor="lime"
              borderRadius="8px"
              overflow="hidden"
              bg="white"
              boxShadow="0 0 0 3px rgba(133,194,42,.18)"
              flexShrink={0}
              w={VALUE_FIELD_WIDTH}
            >
              <Input
                autoFocus
                variant="unstyled"
                flex="1"
                minW={0}
                px="10px"
                py="7px"
                textAlign="right"
                fontSize="14px"
                fontWeight="600"
                sx={{ fontVariantNumeric: "tabular-nums" }}
                placeholder="Value"
                value={draft.dataValue}
                onChange={(e) => setDraft((prev) => ({ ...prev, dataValue: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") cancel();
                }}
              />
              {unitOptions ? (
                <Box position="relative" w={UNIT_SEGMENT_WIDTH} flexShrink={0}>
                  <Box
                    as="select"
                    w="100%"
                    h="100%"
                    appearance="none"
                    border="none"
                    outline="none"
                    fontSize="12.5px"
                    fontWeight="600"
                    fontFamily="inherit"
                    color="gray.600"
                    bg="gray.50"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                    pl={2}
                    pr="20px"
                    value={draft.unit}
                    onChange={(e) => setDraft((prev) => ({ ...prev, unit: e.target.value }))}
                  >
                    {unitOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Box>
                  <Box position="absolute" right="7px" top="50%" transform="translateY(-50%)" pointerEvents="none">
                    <CaretDown size={10} color="#718096" />
                  </Box>
                </Box>
              ) : (
                <Flex
                  w={UNIT_SEGMENT_WIDTH}
                  flexShrink={0}
                  align="center"
                  justify="center"
                  fontSize="12px"
                  color="gray.600"
                  bg="gray.50"
                  borderLeft="1px solid"
                  borderColor="gray.200"
                >
                  {unitParts.base || "—"}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Td>
        <Td w={ACTIONS_SLOT_WIDTH}>
          <Flex justify="flex-end" align="center" gap={2}>
            <IconButton
              size="xs"
              bg="lime"
              color="darkTeal"
              _hover={{ bg: "limeShade.600" }}
              aria-label="Save"
              icon={<Check size={14} weight="bold" />}
              isLoading={isAdding}
              isDisabled={!canSubmit}
              onClick={save}
            />
            <IconButton
              size="xs"
              variant="secondary"
              aria-label="Cancel"
              icon={<X size={12} />}
              onClick={cancel}
            />
          </Flex>
        </Td>
      </Tr>
    );
  }

  return (
    <Tr>
      <Td>{labelCell}</Td>
      <Td>
        <Flex justify="flex-end">
          <Flex
            border="1px solid"
            borderColor="gray.200"
            borderRadius="8px"
            overflow="hidden"
            bg="white"
            flexShrink={0}
            w={VALUE_FIELD_WIDTH}
            cursor={canEdit ? "pointer" : undefined}
            onClick={canEdit ? startEdit : undefined}
            role={canEdit ? "button" : undefined}
            aria-label={canEdit ? `Add a value for ${target.dataKey}` : undefined}
            tabIndex={canEdit ? 0 : undefined}
            onKeyDown={
              canEdit
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      startEdit();
                    }
                  }
                : undefined
            }
            sx={canEdit ? { "&:hover": { borderColor: "lime" }, "&:hover p": { color: "gray.600" } } : undefined}
          >
            <Text
              as="p"
              flex="1"
              minW={0}
              px="10px"
              py="7px"
              textAlign="right"
              fontSize="14px"
              fontWeight="500"
              color="gray.400"
            >
              Not reported
            </Text>
            {/* The unit the structure EXPECTS, shown even with no value: it is what tells a person filling
                this in by hand which unit to report, and what a mismatch will later be reported against. */}
            <Flex
              w={UNIT_SEGMENT_WIDTH}
              flexShrink={0}
              align="center"
              justify="center"
              fontSize="12px"
              color="gray.600"
              bg="gray.50"
              borderLeft="1px solid"
              borderColor="gray.200"
            >
              {unitParts.base || "—"}
            </Flex>
          </Flex>
        </Flex>
      </Td>
      {/* Empty in read mode — see the javadoc above for why this cell stays rather than being removed. */}
      <Td w={ACTIONS_SLOT_WIDTH} />
    </Tr>
  );
}
